/**
 * SDR Stock Manager - Supabase Realtime WebSocket Subscriptions & Echo Suppression
 * @module services/realtime
 */

import { TABLES, SUPABASE_CONFIG, SYNC_CONFIG } from '../config/constants.js';
import { getRealtimeSelfPauseUntil } from '../state/store.js';
import { getSupabaseConfig, getSupabaseClient, fromInventoryRow } from './supabase.js';
import { scheduleVisibleBackgroundSync } from './sync.js';
import { checkActiveItemFormConflict } from '../forms/item-form.js';

let realtimeClient = null;
let realtimeChannel = null;
let realtimeConfigKey = '';
let realtimePullTimer = null;
let realtimeStatusListener = null;

/**
 * Sets a callback to be notified when realtime connection status changes.
 * @param {(statusText: string, isConnected: boolean) => void} listener
 */
export function onRealtimeStatusChange(listener) {
  realtimeStatusListener = listener;
}

/**
 * Triggers status update to registered listeners.
 * @param {string} text
 * @param {boolean} [connected=false]
 */
export function updateRealtimeStatus(text, connected = false) {
  if (typeof realtimeStatusListener === 'function') {
    realtimeStatusListener(text, connected);
  }
}

/**
 * Debounces incoming Realtime Postgres change notifications by 650ms with 1.5s self-echo suppression.
 * @param {any} [payload=null]
 */
export function scheduleRealtimePull(payload = null) {
  if (payload && payload.table === TABLES.INVENTORY) {
    const rawRow = payload.new || payload.old;
    if (rawRow) {
      try {
        const itemModel = fromInventoryRow(rawRow);
        checkActiveItemFormConflict(itemModel);
      } catch (e) {}
    }
  }

  if (Date.now() < getRealtimeSelfPauseUntil()) {
    return; // Self-echo suppression window active
  }

  if (realtimePullTimer) {
    clearTimeout(realtimePullTimer);
  }

  realtimePullTimer = setTimeout(() => {
    realtimePullTimer = null;
    scheduleVisibleBackgroundSync('realtime-pull');
  }, SYNC_CONFIG.REALTIME_DEBOUNCE_MS);
}

/**
 * Stops and unsubscribes from any active Realtime channel.
 */
export function stopRealtimeSync() {
  if (realtimeChannel && realtimeClient && typeof realtimeClient.removeChannel === 'function') {
    try {
      realtimeClient.removeChannel(realtimeChannel);
    } catch (e) {}
  }

  if (realtimeChannel && typeof realtimeChannel.unsubscribe === 'function') {
    try {
      realtimeChannel.unsubscribe();
    } catch (e) {}
  }

  realtimeClient = null;
  realtimeChannel = null;
  realtimeConfigKey = '';

  if (realtimePullTimer) {
    clearTimeout(realtimePullTimer);
    realtimePullTimer = null;
  }
}

/**
 * Subscribes to PostgreSQL change events across all 6 tables on `'sdr-inventory-db-changes'`.
 * @returns {any} Realtime channel instance
 */
export function startRealtimeSync() {
  const { url, anonKey } = getSupabaseConfig();
  if (!url || !anonKey) return null;

  const nextConfigKey = `${url}|${anonKey.slice(0, 12)}`;
  if (realtimeChannel && realtimeConfigKey === nextConfigKey) {
    return realtimeChannel;
  }

  stopRealtimeSync();
  realtimeConfigKey = nextConfigKey;

  const client = getSupabaseClient();
  if (!client || typeof client.channel !== 'function') return null;

  realtimeClient = client;
  realtimeChannel = realtimeClient
    .channel(SUPABASE_CONFIG.REALTIME_CHANNEL)
    .on('postgres_changes', { event: '*', schema: 'public', table: TABLES.INVENTORY }, scheduleRealtimePull)
    .on('postgres_changes', { event: '*', schema: 'public', table: TABLES.TRANSACTIONS }, scheduleRealtimePull)
    .on('postgres_changes', { event: '*', schema: 'public', table: TABLES.APP_SETTINGS }, scheduleRealtimePull)
    .on('postgres_changes', { event: '*', schema: 'public', table: TABLES.STOCK_AUDITS }, scheduleRealtimePull)
    .on('postgres_changes', { event: '*', schema: 'public', table: TABLES.STOCK_OUT_MASTER }, scheduleRealtimePull)
    .on('postgres_changes', { event: '*', schema: 'public', table: TABLES.STOCK_OUT_DETAILS }, scheduleRealtimePull)
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        updateRealtimeStatus('Realtime พร้อมใช้งาน', true);
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        console.warn('Supabase Realtime status error:', status);
        updateRealtimeStatus('Realtime ขัดข้อง', false);
      }
    });

  return realtimeChannel;
}

/**
 * Ensures realtime is actively connected, re-subscribing if dropped (essential for Safari WebKit after tab wake).
 */
export function ensureRealtimeConnected() {
  if (!realtimeChannel) {
    return startRealtimeSync();
  }
  return realtimeChannel;
}

/**
 * Gets the current active realtime channel.
 * @returns {any}
 */
export function getRealtimeChannel() {
  return realtimeChannel;
}

/**
 * Gets the current active realtime client.
 * @returns {any}
 */
export function getRealtimeClient() {
  return realtimeClient;
}

